import { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { getCurrencySymbol, centsToDollars, parseCurrencyInput } from '../../lib/currency';
import type { CurrencyCode } from '../../types';

interface AmountInputProps {
  value: number; // In cents
  onChange: (cents: number) => void;
  currency: CurrencyCode;
  label?: string;
  placeholder?: string;
  id?: string;
}

export function AmountInput({
  value,
  onChange,
  currency,
  label,
  placeholder = '0.00',
  id = 'amount',
}: AmountInputProps) {
  const [displayValue, setDisplayValue] = useState('');

  // Sync display value when external value changes
  useEffect(() => {
    if (value === 0) {
      setDisplayValue('');
    } else {
      setDisplayValue(centsToDollars(value));
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Only allow numbers, dots, and commas
    const sanitized = inputValue.replace(/[^\d.,]/g, '');
    setDisplayValue(sanitized);
  };

  const handleBlur = () => {
    const cents = parseCurrencyInput(displayValue);
    onChange(cents);
    if (cents === 0) {
      setDisplayValue('');
    } else {
      setDisplayValue(centsToDollars(cents));
    }
  };

  const symbol = getCurrencySymbol(currency);

  return (
    <div className="space-y-2">
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
          {symbol}
        </span>
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="pl-8"
        />
      </div>
    </div>
  );
}
